import React, { useState, useEffect, useMemo } from 'react'
import {
    Box,
    Dialog,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material'
import { Menu as MenuIcon, Close as CloseIcon, LightMode as SunIcon, DarkMode as MoonIcon } from '@mui/icons-material'
import styles from './Sidebar.module.scss'
import CasaLogo from '../../../shared/logo/CasaLogo'
import { MENU_PERMISSIONS, SidebarMenu, sidebarList } from '../constants/Sidebar'
import MenuItem from '../MenuItem/MenuItem'
import _, { get } from 'lodash'
import Logout from '../../../shared/components/Logout/Logout'
import { IconButton } from '@mui/material'
import keycloak from '../../../keycloak'
import { NavigationLinks } from '../constants/NavigationLinks'
import { useLocation, useNavigate } from 'react-router-dom'

import useUser from '../../../hooks/useUser'
import { useThemeContext } from '../../../contexts/ThemeContext'
import { useTenantPermissions } from '../../Tenant360/permissions'

interface SidebarProps {
    isExpanded?: boolean;
    onToggle?: (expanded: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isExpanded: propExpanded, onToggle }) => {
    const { pathname } = useLocation()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const { currentUser } = useUser()
    const { mode, toggleTheme } = useThemeContext()
    const { can } = useTenantPermissions()

    // Menus mapped in MENU_PERMISSIONS render only for users holding that permission
    const visibleMenus = useMemo(
        () =>
            sidebarList.filter((menu) => {
                const perm = MENU_PERMISSIONS[menu as SidebarMenu]
                return !perm || [perm].flat().some(can)
            }),
        [can] // eslint-disable-line react-hooks/exhaustive-deps
    )

    const currentSideMenu: any = sidebarList.find((sl) => {
        const link = get(NavigationLinks, sl)
        if (link === '/') {
            return pathname === '/' || pathname.startsWith('/tenants') || pathname.startsWith('/tenant')
        }
        return pathname.startsWith(link)
    })

    const [selectedMenu, setSelectedMenu] = useState(currentSideMenu)
    const [localExpanded, setLocalExpanded] = useState(false)
    const isExpanded = propExpanded ?? localExpanded;

    const handleExpand = (val: boolean) => {
        setLocalExpanded(val);
        onToggle?.(val);
    }
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const navigate = useNavigate()

    const logout = () => {
        keycloak.logout()
    }

    const goToPage = (menuName: string) => {
        setSelectedMenu(menuName)
        navigate(_.get(NavigationLinks, menuName))
        if (isMobile) {
            setMobileMenuOpen(false)
        }
    }

    useEffect(() => {
        setSelectedMenu(currentSideMenu)
    }, [pathname, currentSideMenu])

    if (isMobile) {
        return (
            <>
                <Box className={styles.mobileBottomNav}>
                    <button
                        className={styles.mobileMenuToggle}
                        onClick={() => setMobileMenuOpen(true)}
                        aria-label="Open menu"
                    >
                        <MenuIcon />
                    </button>

                    <Box className={styles.mobileIconBar}>
                        {visibleMenus.slice(0, 4).map((element) => (
                            <MenuItem
                                key={element}
                                menuName={element}
                                goToPage={goToPage}
                                selectedMenu={selectedMenu}
                                isIconOnly={true}
                            />
                        ))}
                    </Box>

                    <IconButton
                        onClick={toggleTheme}
                        color="inherit"
                        className={styles.themeToggle}
                        aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                    >
                        {mode === 'dark' ? <SunIcon /> : <MoonIcon />}
                    </IconButton>
                </Box>

                <Dialog
                    open={mobileMenuOpen}
                    onClose={() => setMobileMenuOpen(false)}
                    PaperProps={{
                        className: styles.mobileDialog,
                    }}
                >
                    <Box className={styles.mobileDialogContent}>
                        <Box className={styles.mobileDialogHeader}>
                            <CasaLogo className={styles.logo} />
                            <button
                                onClick={() => setMobileMenuOpen(false)}
                                className={styles.closeButton}
                                aria-label="Close menu"
                            >
                                <CloseIcon />
                            </button>
                        </Box>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                            sx={{
                                width: '100%',
                                px: 2,
                                mt: -2,
                                fontWeight: 500,
                            }}
                        >
                            Hi,{' '}
                            {currentUser?._internalUserEmail?.split('@')[0] ||
                                'Super User'}
                            !
                        </Typography>

                        <Box className={styles.mobileDialogMenu}>
                            {_.map(visibleMenus, (element) => (
                                <MenuItem
                                    key={element}
                                    menuName={element}
                                    goToPage={goToPage}
                                    selectedMenu={selectedMenu}
                                    isIconOnly={false}
                                />
                            ))}
                        </Box>

                        <Box
                            className={styles.mobileDialogFooter}
                        >
                            <Logout
                                logout={() => logout()}
                                isIconOnly={false}
                            />
                        </Box>
                    </Box>
                </Dialog>
            </>
        )
    }

    return (
        <Box
            id={styles.sidebar}
            className={isExpanded ? styles.expanded : styles.collapsed}
            onMouseEnter={() => handleExpand(true)}
            onMouseLeave={() => handleExpand(false)}
        >
            <IconButton
                onClick={toggleTheme}
                className={styles.desktopThemeToggle}
                aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                sx={{
                    position: 'absolute',
                    top: 16,
                    right: isExpanded ? 16 : 'auto',
                    left: isExpanded ? 'auto' : '50%',
                    transform: isExpanded ? 'none' : 'translateX(-50%)',
                    transition: 'all 0.3s ease',
                    color: 'text.secondary',
                    zIndex: 10,
                    '&:hover': {
                        color: 'primary.main',
                    }
                }}
            >
                {mode === 'dark' ? <SunIcon /> : <MoonIcon />}
            </IconButton>
            <Box className={styles.header}>
                <CasaLogo className={styles.logo} />
                {isExpanded && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        noWrap
                        sx={{
                            width: '100%',
                            textAlign: 'center',
                            px: 1,
                            mb: 1.5,
                            mt: 1,
                            fontWeight: 500,
                        }}
                    >
                        Hi,{' '}
                        {currentUser?._internalUserEmail?.split('@')[0] ||
                            'Super User'}
                        !
                    </Typography>
                )}
            </Box>
            <Box
                className={styles.sidebarList}
                sx={{ flex: 1, overflowY: 'auto' }}
            >
                {_.map(visibleMenus, (element) => (
                    <MenuItem
                        key={element}
                        menuName={element}
                        goToPage={goToPage}
                        selectedMenu={selectedMenu}
                        isIconOnly={!isExpanded}
                    />
                ))}
            </Box>

            <Box
                className={styles.logout}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    mt: 'auto',
                }}
            >
                <Logout logout={() => logout()} isIconOnly={!isExpanded} />
            </Box>
        </Box>
    )
}

export default Sidebar
